import { ChangeDetectionStrategy, Component, effect, inject, NgZone, OnDestroy, OnInit, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { DataService } from '../../../core/services/data.service';
import { AuthService } from '../../../core/services/auth.service';
import { LayoutService } from '../../../core/services/layout.service';

@Component({
  selector: 'app-app-layout-header',
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
})
export class AppLayoutHeader implements OnInit, OnDestroy {
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private ngZone     = inject(NgZone);
  readonly layoutService = inject(LayoutService);

  readonly profile = toSignal(this.dataService.profile$, { initialValue: null });
  readonly isScrolled = signal(false);
  readonly showLogoutModal = signal(false);
  /** Flips to true when the profile <img> fires an error — shows initials instead. */
  readonly avatarError = signal(false);

  constructor() {
    // Reset the error flag whenever the profile image URL changes so a newly
    // uploaded photo is attempted again instead of staying on initials.
    effect(() => {
      const url = this.profile()?.profileImagePath;
      if (url) this.avatarError.set(false);
    });
  }

  private scrollListener!: () => void;

  ngOnInit(): void {
    // Run outside Angular zone so scroll events don't trigger change detection
    this.ngZone.runOutsideAngular(() => {
      this.scrollListener = () => {
        const scrolled = window.scrollY > 10;
        // Only enter zone and update signal when the value actually changes
        if (scrolled !== this.isScrolled()) {
          this.ngZone.run(() => this.isScrolled.set(scrolled));
        }
      };
      window.addEventListener('scroll', this.scrollListener, { passive: true });
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.scrollListener);
  }

  readonly getInitials = computed(() => {
    const p = this.profile();
    if (!p) return '?';
    const f = p.firstName?.trim() || p.fullName?.split(' ')[0] || '';
    const l = p.lastName?.trim() || p.fullName?.split(' ')[1] || '';
    return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || '?';
  });

  openLogoutModal(): void {
    this.showLogoutModal.set(true);
  }

  closeLogoutModal(): void {
    this.showLogoutModal.set(false);
  }

  confirmLogout(): void {
    this.showLogoutModal.set(false);
    this.authService.logout();
  }

  onModalBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.closeLogoutModal();
    }
  }

  /** Expand sidebar from collapsed state (desktop) */
  onToggle(): void {
    this.layoutService.toggleCollapse();
  }

  /** Open sidebar drawer on mobile */
  onMobileToggle(): void {
    this.layoutService.toggle();
  }

  onAvatarError(): void {
    this.avatarError.set(true);
  }
}
